import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Building2, 
  Bed, 
  Bath,
  MapPin,
  Trash2,
  Eye,
  Edit,
  Folder,
  Camera,
  Share2
} from 'lucide-react';
import PropertyDialog from '@/components/dialogs/PropertyDialog';
import PropertyFilesDialog from '@/components/dialogs/PropertyFilesDialog';
import SharePropertyDialog from '@/components/dialogs/SharePropertyDialog';
import { deleteProperty } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PhotoGallery } from '@/components/PhotoGallery';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const defaultImages = [
  'https://images.unsplash.com/photo-1756802156662-d0fa771ba255?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB2YWNhdGlvbiUyMHZpbGxhJTIwZXh0ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzY4MTEzNjA5fDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1760067537640-6ffab10b27d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB2YWNhdGlvbiUyMHZpbGxhJTIwZXh0ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzY4MTEzNjA5fDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1757439402384-c0afc91fda20c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB2YWNhdGlvbiUyMHZpbGxhJTIwZXh0ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzY4MTEzNjA5fDA&ixlib=rb-4.1.0&q=85',
];

export const PropertiesView = ({ properties, onRefresh, onSelectProperty }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [filesProperty, setFilesProperty] = useState(null);
  const [shareProperty, setShareProperty] = useState(null);

  const handleSavePhotos = async (propertyId, photos) => {
    try {
      await api.put(`/properties/${propertyId}`, { photos });
      onRefresh?.();
    } catch (_) {
      toast.error('Failed to save photos');
    }
  };

  const handleDelete = async () => {
    if (!propertyToDelete) return;
    
    try {
      await deleteProperty(propertyToDelete.id);
      toast.success('Property deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete property');
    } finally {
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const confirmDelete = (property, e) => {
    e.stopPropagation();
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="properties-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Properties
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your vacation rental and other properties.
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-property-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Property
        </Button>
      </div>

      {/* Properties Grid */}
      {properties.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No properties yet</h3>
            <p className="text-slate-500 mb-4">Add your first property to get started</p>
            <Button 
              onClick={() => setDialogOpen(true)}
              data-testid="add-first-property-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property, idx) => (
            <Card 
              key={property.id}
              className="bg-white border border-slate-100 overflow-hidden card-hover cursor-pointer group"
              onClick={() => onSelectProperty(property)}
              data-testid={`property-card-${property.id}`}
            >
              <div className="relative h-48 bg-slate-100">
                <img
                  src={property.image_url || defaultImages[idx % defaultImages.length]}
                  alt={property.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = defaultImages[idx % defaultImages.length];
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-semibold text-lg font-manrope">
                    {property.name}
                  </h3>
                  <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {property.address}
                  </p>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Bed className="h-4 w-4" />
                      {property.bedrooms} beds
                    </span>
                    <span className="flex items-center gap-1">
                      <Bath className="h-4 w-4" />
                      {property.bathrooms} baths
                    </span>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectProperty(property);
                      }}
                      data-testid={`view-property-${property.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilesProperty(property);
                      }}
                      title="Manage files"
                      data-testid={`files-property-${property.id}`}
                    >
                      <Folder className="h-4 w-4" />
                    </Button>
                    <span onClick={(e) => e.stopPropagation()}>
                      <PhotoGallery
                        photos={property.photos || []}
                        onSave={(next) => handleSavePhotos(property.id, next)}
                        kind="property_photo"
                        title={`${property.name} — Photos`}
                        showBeforeAfter={false}
                        testIdPrefix={`prop-gallery-${property.id}`}
                        trigger={
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Photo gallery"
                            data-testid={`photos-property-${property.id}`}
                          >
                            <Camera className="h-4 w-4" />
                          </Button>
                        }
                      />
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareProperty(property);
                      }}
                      title={property.is_public ? 'Manage public showcase' : 'Publish public showcase'}
                      className={property.is_public ? 'text-emerald-600 hover:text-emerald-700' : ''}
                      data-testid={`share-property-${property.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingProperty(property);
                        setDialogOpen(true);
                      }}
                      data-testid={`edit-property-${property.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => confirmDelete(property, e)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-property-${property.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {property.description && (
                  <p className="text-sm text-slate-500 mt-3 line-clamp-2">
                    {property.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <PropertyDialog
        open={dialogOpen}
        editingProperty={editingProperty}
        onClose={() => {
          setDialogOpen(false);
          setEditingProperty(null);
          onRefresh();
        }}
      />

      <PropertyFilesDialog
        open={!!filesProperty}
        onOpenChange={(o) => {
          if (!o) setFilesProperty(null);
        }}
        property={filesProperty}
      />

      <SharePropertyDialog
        property={shareProperty}
        open={!!shareProperty}
        onOpenChange={(o) => { if (!o) setShareProperty(null); }}
        onSaved={() => { setShareProperty(null); onRefresh?.(); }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{propertyToDelete?.name}&rdquo;? This will also delete all associated bookings, cleaning tasks, and maintenance tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PropertiesView;
